
import { Component, Vue, Prop, Watch } from "vue-property-decorator";

import { UserRoles } from "@/utils/UserConstants";
import { EMPTY_STRING } from "@/utils/Constants";

@Component({
	name: "UserDetailsModal"
})
export default class UserDetailsModal extends Vue {
	@Prop(Boolean) readonly visible!: boolean;
	@Prop(Object) readonly user: any;
	@Prop(Array) readonly reinsurers: any;
	@Prop(Array) readonly businesses: any;
	@Prop(Function) readonly refreshUsers!: () => void;

	ROLE_ADMIN = UserRoles.ROLE_ADMIN;
	ROLE_REINSURER = UserRoles.ROLE_REINSURER;
	ROLE_SUBSCRIPTOR = UserRoles.ROLE_SUBSCRIPTOR;

	isVisible: boolean = false;

	editableUser: any = {
		id: EMPTY_STRING,
		authExternalId: null,
		authStatusId: null,
		fullname: EMPTY_STRING,
		firstName: EMPTY_STRING,
		lastName: EMPTY_STRING,
		email: EMPTY_STRING,
		roleId: EMPTY_STRING,
		reinsurerId: EMPTY_STRING,
		businessId: EMPTY_STRING,
		can_do_request: 0,
		mail_notification: 0,
		request_notification: 0,
		claim_notification: 0
	};
	validationErrors: any = {
		fullname: false,
		firstName: false,
		lastName: false,
		email: false,
		roleId: false,
		reinsurerId: false,
		emptyReinsurer: false
	}

	@Watch('user', { immediate: true, deep: true })
	onUserChange(selectedUser: any) {

		if (selectedUser) {
			const { admin, subscriptor } = selectedUser;

			this.editableUser = {
				id: selectedUser.id,
				authExternalId: selectedUser.authExternalId ? selectedUser.authExternalId : null,
				authStatusId: selectedUser.authStatusId ? selectedUser.authStatusId : null,
				fullname: admin != null ? admin.fullname : subscriptor.fullname,
				firstName: selectedUser.firstName,
				lastName: selectedUser.lastName,
				email: selectedUser.email,
				roleId: selectedUser.roleId,
				reinsurerId: admin != null ? admin.reinsurerId : null,
			};

			if (subscriptor) {
				this.editableUser = {
					...this.editableUser,
					businessId: subscriptor.businessId,
					can_do_request: subscriptor.can_do_request != null && subscriptor.can_do_request ? 1 : 0,
					mail_notification: subscriptor.mail_notification != null && subscriptor.mail_notification ? 1 : 0,
					request_notification: subscriptor.request_notification != null && subscriptor.request_notification ? 1 : 0,
					claim_notification: subscriptor.claim_notification != null && subscriptor.claim_notification ? 1 : 0
				}
			}
		}
	}

	async refreshOktaData() {
		try {

			if (this.isNullOrEmpty(this.editableUser.firstName) && this.isNullOrEmpty(this.editableUser.lastName)) {
				(this.$root as any).notify("Actualice el nombre y apellido para actualizar los datos de Okta.", "warn")
				return;
			}

			const response: any = await (this.$root as any)._put(`users/${this.editableUser.id}/auth/refresh`);

			this.editableUser.authExternalId = response.authExternalId;
			this.editableUser.authStatusId = response.authStatusId;

			this.$emit('refreshUsers');

			(this.$root as any).notify("Datos de Okta actualizados correctamente.");
			return;
		} catch (error) {
			(this.$root as any).notify("Error al actualizar los datos de Okta.", "error");
		}
	}

	async activate() {
		try {
			await (this.$root as any)._post(`users/${this.editableUser.id}/auth/activate`, { sendEmail: true });

			(this.$root as any).notify("El usuario fue activado con éxito. Se le enviará un email para configurar su cuenta.");
		} catch (error) {
			(this.$root as any).notify("Error al activar el usuario.", "error");
		}
	}

	async reactivate() {
		try {
			await (this.$root as any)._post(`users/${this.editableUser.id}/auth/reactivate`, { sendEmail: true });

			(this.$root as any).notify("El usuario fue reactivado con éxito. Se le enviará un email para configurar su cuenta.");
		} catch (error) {
			(this.$root as any).notify("Error al reactivar el usuario.", "error");
		}
	}

	async resetPassword() {
		try {
			await (this.$root as any)._post(`users/${this.editableUser.id}/auth/reset-password`, { sendEmail: true});

			(this.$root as any).notify("Se ha reestablecido la contraseña del usuario correctamente.");
		} catch (error) {
			(this.$root as any).notify("Error al restablecer la contraseña.", "error");
		}
	}

	async unlock() {
		try {
			await (this.$root as any)._post(`users/${this.editableUser.id}/auth/unlock`);

			(this.$root as any).notify("El usuario ha sido desbloqueado correctamente.");
		} catch (error) {
			(this.$root as any).notify("Error al desbloquear el usuario.", "error");
		}
	}

	cancel() {
		this.hide();
	}

	show() {
		this.isVisible = true;
	}

	hide() {
		this.isVisible = false;
	}

	async updateAndClose() {
		if (this.validateData(this.editableUser)) {

			try {
				await (this.$root as any)._patch(`users/${(this.editableUser as any).id}`, this.editableUser);

				this.$emit('refreshUsers');

				(this.$root as any).notify(`El usuario fue actualizado con éxito.`);

				this.hide();
			} catch (e: any) {
				let errorMessage;

				switch (e.code) {
					case "EMAIL_EXISTS":
						errorMessage = "Ya existe un usuario con el email ingresado.";
						break;
					case "ADMIN_USER_EXISTS":
						errorMessage = "Ya existe un usuario administrador con el email ingresado";
						break;
					case "SUBSCRIPTOR_USER_EXISTS":
						errorMessage = "El mail ingresado esta asociado a un usuario subscriptor.<br/>Para continuar, elimine el suscriptor o intente con otro email.";
						break;
					case "INVALID_ROLE":
						errorMessage = "El rol ingresado no existe o no es válido";
						break;
					case "NO_REINSURER_SELECTED":
						errorMessage = "Debe seleccionar una reaseguradora para el rol REASEGURADOR.";
						break;
					default:
						errorMessage = `No se pudo actualizar el usuario.`;
						break;
				}

				(this.$root as any).notify(errorMessage, "error");
			}
		}
	}

	validateData(user: any) {
		const email = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
		let validated = true;

		if (this.isNullOrEmpty(user.firstName)) {
			this.validationErrors.firstName = true;
			validated = false;
		} else this.validationErrors.firstName = false;

		if (this.isNullOrEmpty(user.lastName)) {
			this.validationErrors.lastName = true;
			validated = false;
		} else this.validationErrors.lastName = false;

		if (this.isNullOrEmpty(user.email)) {
			this.validationErrors.email = true;
			validated = false;
		} else this.validationErrors.email = false

		if (user.roleId === '') {
			this.validationErrors.roleId = true;
			validated = false;
		} else this.validationErrors.roleId = false;

		if (!validated) {
			(this.$root as any).notify("Hay campos requeridos faltantes. Por favor, verifique y vuelva a intentarlo.", "error")
			return validated;
		}

		if (!email.test(user.email)) {
			this.validationErrors.email = true;
			(this.$root as any).notify("El campo email no tiene un formato correcto. Verifique y vuelva a intentarlo.", "error")
			return false;
		}

		if (user.roleId == this.ROLE_REINSURER.toString() && (user.reinsurerId === null || user.reinsurerId === '')) {
			this.validationErrors.emptyReinsurer = true;
			(this.$root as any).notify("Debe seleccionar una reaseguradora para el rol reasegurador.", "error");
			return false;
		} else {
			this.validationErrors.emptyReinsurer = false;
		}

		if (user.roleId == this.ROLE_SUBSCRIPTOR.toString() && (user.businessId === null || user.businessId === '')) {
			this.validationErrors.emptyBusiness = true;
			(this.$root as any).notify("Debe seleccionar una empresa para el rol suscriptor.", "error");
			return false;
		} else {
			this.validationErrors.emptyBusiness = false;
		}

		return validated;
	}

	getName(user: any) {
		if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
		else return user.fullname
	}

	getAuthStatus(user: any) {
		const statuses = {
			null: { class: "no-data", text: "Sin datos" },
			1: { class: "pending", text: "Pendiente" },
			2: { class: "pending", text: "Pendiente" },
			3: { class: "active", text: "Activo" }
		};

		if (user && user.authStatusId >= 4 && user.authStatusId <= 8) {
			return { class: "inactive", text: "Inactivo" };
		}
		console.log(user);
		return statuses[user.authStatusId] || { class: "no-data", text: "Sin datos" };
	}

	isNullOrEmpty(value: string | null | undefined) { return value == null || (typeof value === 'string' && value.trim().length === 0) }
}

